// src/components/Search/CategoryCarousel.js

import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { FaHeart, FaRegHeart, FaChevronLeft, FaChevronRight, FaStar } from 'react-icons/fa'; // Import FaStar
import { Swiper, SwiperSlide } from 'swiper/react';
import { FreeMode, Navigation } from 'swiper/modules'; // Swiper 10+
import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';

const DEFAULT_IMAGE = 'https://via.placeholder.com/140x207?text=No+Image';

export default function CategoryCarousel({
  category,
  items,
  onItemClick,
  favorites,
  onAddFavorite,
  onRemoveFavorite
}) {
  const [selectedItem, setSelectedItem] = useState(null);

  // Refs to our custom nav buttons
  const prevBtnRef = useRef(null);
  const nextBtnRef = useRef(null);

  const getUniqueId = (item) => item.imdbId || item.tmdbId || item.id || item.url;

  const isFavorited = (item) =>
    favorites.some((fav) => fav.id === getUniqueId(item));

  const handleItemClick = (item) => {
    setSelectedItem(getUniqueId(item));
    onItemClick(item);
  };

  const handleImageError = (e) => {
    e.target.src = DEFAULT_IMAGE;
  };

  return (
    <div className="w-full px-4 space-y-8">
      {/* Category Title */}
      <h3 className="text-3xl font-bold text-accent mb-6 text-left">
        {category}
      </h3>

      <Swiper
        modules={[Navigation, FreeMode]}
        freeMode={{ enabled: true, momentum: true }}
        slidesPerView="auto"
        spaceBetween={16}
        preventClicksPropagation={true}
        preventClicks={false}
        onBeforeInit={(swiper) => {
          if (!swiper.params.navigation) swiper.params.navigation = {};
          swiper.params.navigation.prevEl = prevBtnRef.current;
          swiper.params.navigation.nextEl = nextBtnRef.current;
        }}
        className="group relative pb-8"
      >
        {items.map((item) => {
          const uniqueId = getUniqueId(item);
          const favorited = isFavorited(item);

          return (
            <SwiperSlide
              key={uniqueId}
              style={{ width: '160px' }}
            >
              <div
                onClick={() => handleItemClick(item)}
                className={`
                  relative bg-glassOverlay rounded-lg shadow-lg 
                  hover:shadow-2xl transition-transform duration-200 cursor-pointer
                  ${selectedItem === uniqueId ? 'border-4 border-accentGreen' : ''}
                `}
                style={{ height: '240px' }}
              >
                {/* Poster Image */}
                <img
                  src={
                    item.photoUrl && item.photoUrl !== 'No Image'
                      ? item.photoUrl
                      : DEFAULT_IMAGE
                  }
                  alt={item.title}
                  onError={handleImageError}
                  className="w-full h-full object-cover rounded-lg"
                  loading="lazy"
                />

                {/* Heart / Favorite */}
                <div className="absolute top-2 right-2 z-10">
                  {favorited ? (
                    <FaHeart
                      className="text-red-500 hover:text-red-600 cursor-pointer"
                      size={20}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveFavorite(uniqueId);
                      }}
                      aria-label="Remove from favorites"
                    />
                  ) : (
                    <FaRegHeart
                      className="text-white hover:text-red-500 cursor-pointer transition-opacity duration-200"
                      size={20}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddFavorite(item);
                      }}
                      aria-label="Add to favorites"
                    />
                  )}
                </div>

                {/* IMDb Rating with yellow star */}
                {item.imdbRating && item.imdbRating !== 'N/A' && (
                  <div className="absolute top-2 left-2 bg-glassCircle text-white text-xs font-semibold px-2 py-1 rounded-full shadow-lg z-10 flex items-center">
                    <FaStar className="text-yellow-400 mr-1" size={12} />
                    {item.imdbRating}
                  </div>
                )}

                {/* Title & Type Overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 
                                backdrop-blur-md p-2 rounded-b-lg">
                  <h4 className="text-sm font-medium text-white truncate">
                    {item.title}
                  </h4>
                  <p className="text-xs text-gray-300">
                    {item.type === 'tv' ? 'TV Show' : 'Movie'}
                  </p>
                </div>
              </div>
            </SwiperSlide>
          );
        })}

        {/* Custom Arrow Buttons */}
        <button
          ref={prevBtnRef}
          className="hidden group-hover:flex items-center justify-center 
                     w-10 h-10 bg-black/50 text-white rounded-full 
                     absolute top-1/2 -translate-y-1/2 left-2 z-10
                     hover:bg-black transition"
          aria-label="Previous"
        >
          <FaChevronLeft />
        </button>

        <button
          ref={nextBtnRef}
          className="hidden group-hover:flex items-center justify-center 
                     w-10 h-10 bg-black/50 text-white rounded-full 
                     absolute top-1/2 -translate-y-1/2 right-2 z-10
                     hover:bg-black transition"
          aria-label="Next"
        >
          <FaChevronRight />
        </button>
      </Swiper>
    </div>
  );
}

CategoryCarousel.propTypes = {
  category: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  onItemClick: PropTypes.func.isRequired,
  favorites: PropTypes.array.isRequired,
  onAddFavorite: PropTypes.func.isRequired,
  onRemoveFavorite: PropTypes.func.isRequired,
};
